import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  emailRegex,
  InputPlaceHolder,
  numberRegex,
  SelectPlaceHolder,
  validationMessages,
  zipcodeRegex,
} from "../../Components/constants/validation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { listOfCity, listOfCountry, listOfState } from "Api/common";
import { createStaff, updateStaff } from "Api/staff";
import {
  AlternatePhoneNumberLabel,
  BillingAddressLabel,
  CityLabel,
  CountryLabel,
  CustomerLabel,
  CustomerNameLabel,
  CustomerType,
  CustomerTypeLabel,
  editCustomerLabel,
  EmailLabel,
  newCustomerLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  SameAsBillingAddressLabel,
  SameAsBillingAddressTooltipLabel,
  ShippingAddressLabel,
  StateLabel,
  StreetLabel,
} from "Components/constants/Staff";
import { viewCustomer } from "Api/customer";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import { gstinLabel, organizationLabel } from "Components/constants/Vendor";

const validationSchema = yup.object({
  customerType: yup
    .string()
    .required(validationMessages.required(CustomerTypeLabel)),
  customerName: yup
    .string()
    .required(validationMessages.required(CustomerNameLabel)),
  phoneNumber: yup
    .string()
    .required(validationMessages.required(PhoneNumberLabel))
    .matches(
      numberRegex,
      validationMessages.contactLength(PhoneNumberLabel, 10)
    ),
  alternatePhone: yup
    .string()
    .matches(
      numberRegex,
      validationMessages.contactLength(AlternatePhoneNumberLabel, 10)
    ),
  email: yup
    .string()
    .required(validationMessages.required(EmailLabel))
    .matches(emailRegex, validationMessages.format(EmailLabel)),
  gst: yup.string().nullable().max(15, validationMessages.maxLength(gstinLabel, 15)),
  billingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
  shippingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
});

const AddCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? editCustomerLabel : newCustomerLabel;
  const [loader, setLoader] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [viewStaffData, setViewStaffData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);

  const fetchCountry = () => {
    // setLoader(true);
    let data;
    const payload = {};
    listOfCountry(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };
  const fetchState = (id: number) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        country_id: id,
      },
    };
    listOfState(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };
  const fetchCity = (id: any) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        state_id: id,
      },
    };
    listOfCity(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerType: id ? viewStaffData?.customer_type : "",
      customerName: id ? viewStaffData?.name : "",
      phoneNumber: id ? viewStaffData?.contact_no || "" : "",
      email: id ? viewStaffData?.email || "" : "",
      alternatePhone: id ? viewStaffData?.alternative_contact_no || "" : "",
      organization: id ? viewStaffData?.bankDetail?.organization : null,
      gst: id ? viewStaffData?.bankDetail?.gstin_no : null,
      billingStreet: id ? viewStaffData?.address?.billing_address_1 || "" : "",
      billingCountry: id ? viewStaffData?.address?.billing_country_id : "",
      billingState: id ? viewStaffData?.address?.billing_state_id : "",
      billingCity: id ? viewStaffData?.address?.billing_city_id : "",
      billingPostalCode: id
        ? viewStaffData?.address?.billing_postal_code || ""
        : "",
      shippingStreet: id
        ? viewStaffData?.address?.shipping_address_1 || ""
        : "",
      shippingCountry: id ? viewStaffData?.address?.shipping_country_id : "",
      shippingState: id ? viewStaffData?.address?.shipping_state_id : "",
      shippingCity: id ? viewStaffData?.address?.shipping_city_id : "",
      shippingPostalCode: id
        ? viewStaffData?.address?.shipping_postal_code || ""
        : "",
    },
    validationSchema,
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        name: values.customerName,
        contact_no: String(values.phoneNumber),
        email: values.email,
        alternative_contact_no: String(values.alternatePhone),
        customer_type: values.customerType,
        type_of_user: CustomerLabel,
        staffBankDetail: {
          organization: values.organization,
          gstin_no: values.gst,
        },
        staffAddress: {
          billing_address_1: values.billingStreet,
          billing_country_id: values.billingCountry || null,
          billing_state_id: values.billingState || null,
          billing_city_id: values.billingCity || null,
          billing_postal_code: values.billingPostalCode || null,
          shipping_same_as_billing: sameAsBilling,
          shipping_address_1: values.shippingStreet,
          shipping_country_id: values.shippingCountry || null,
          shipping_state_id: values.shippingState || null,
          shipping_city_id: values.shippingCity || null,
          shipping_postal_code: values.shippingPostalCode || null,
        },
      };
      if (id) {
        updateStaff(Number(id), payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/customer");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createStaff(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/customer");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchViewRole = () => {
    if (id) {
      setLoader(true);
      viewCustomer(Number(id))
        .then((response) => {
          if (checkStatusCodeSuccess(response.statusCode)) {
            const roleData = response.data;
            setViewStaffData(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    setSameAsBilling(viewStaffData?.address?.shipping_same_as_billing);
    if (viewStaffData?.address?.billing_country_id) {
      fetchState(Number(viewStaffData?.address?.billing_country_id));
    }
    if (viewStaffData?.address?.billing_state_id) {
      fetchCity(Number(viewStaffData?.address?.billing_state_id));
    }
    if (viewStaffData?.address?.shipping_country_id) {
      fetchState(Number(viewStaffData?.address?.shipping_country_id));
    }
    if (viewStaffData?.address?.shipping_state_id) {
      fetchCity(Number(viewStaffData?.address?.shipping_state_id));
    }
  }, [viewStaffData]);

  useEffect(() => {
    if (id) {
      fetchViewRole();
    }
    fetchCountry();
  }, []);

  const handleSameAsBilling = () => {
    setSameAsBilling(!sameAsBilling);
    if (!sameAsBilling) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: prevValues.billingStreet,
        shippingCountry: prevValues.billingCountry,
        shippingState: prevValues.billingState,
        shippingCity: prevValues.billingCity,
        shippingPostalCode: prevValues.billingPostalCode,
      }));
    } else {
      formik.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: "",
        shippingCountry: "",
        shippingState: "",
        shippingCity: "",
        shippingPostalCode: "",
      }));
    }
  };

  useEffect(() => {
    if (sameAsBilling) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: prevValues.billingStreet,
        shippingCountry: prevValues.billingCountry,
        shippingState: prevValues.billingState,
        shippingCity: prevValues.billingCity,
        shippingPostalCode: prevValues.billingPostalCode,
      }));
    }
  }, [
    formik.values.billingStreet,
    formik.values.billingCountry,
    formik.values.billingState,
    formik.values.billingCity,
    formik.values.billingPostalCode,
    sameAsBilling,
  ]);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? editCustomerLabel : newCustomerLabel}
          </span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/customer")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="customerType"
                    label={CustomerTypeLabel}
                    options={CustomerType}
                    value={formik.values.customerType}
                    placeholder={SelectPlaceHolder(CustomerTypeLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.customerType}
                    touched={formik.touched.customerType}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="customerName"
                    label={CustomerNameLabel}
                    placeholder={InputPlaceHolder(CustomerNameLabel)}
                    value={formik.values.customerName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.customerName}
                    touched={formik.touched.customerName}
                    required
                  />
                </div>

                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="phoneNumber"
                    type="number"
                    label={PhoneNumberLabel}
                    placeholder={InputPlaceHolder(PhoneNumberLabel)}
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.phoneNumber}
                    maxLength={10}
                    touched={formik.touched.phoneNumber}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="email"
                    label={EmailLabel}
                    placeholder={InputPlaceHolder(EmailLabel)}
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                    required
                  />
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="alternatePhone"
                    type="number"
                    label={AlternatePhoneNumberLabel}
                    placeholder={InputPlaceHolder(AlternatePhoneNumberLabel)}
                    value={formik.values.alternatePhone}
                    maxLength={10}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.alternatePhone}
                    touched={formik.touched.alternatePhone}
                  />
                </div>
                {/* <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="paymentMethod"
                    label={PaymentMethodLabel}
                    options={PaymentType}
                    value={formik.values.paymentMethod}
                    placeholder={SelectPlaceHolder(PaymentMethodLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.paymentMethod}
                    touched={formik.touched.paymentMethod}
                  />
                </div> */}
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5">{BillingAddressLabel}</div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="organization"
                    label={organizationLabel}
                    placeholder={InputPlaceHolder(organizationLabel)}
                    type="text"
                    value={formik.values.organization}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.organization}
                    touched={formik.touched.organization}
                  />
                </div>
                <div className="col-3 mt-3">
                  <BaseInput
                    name="gst"
                    label={gstinLabel}
                    placeholder={InputPlaceHolder(gstinLabel)}
                    type="text"
                    value={formik.values.gst}
                    onChange={(e) => {
                      if (e.target.value.length < 16) {                        
                        formik.handleChange(e);
                      }
                    }
                    }
                    handleBlur={formik.handleBlur}
                    error={formik.errors.gst}
                    touched={formik.touched.gst}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="billingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.billingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingStreet}
                    touched={formik.touched.billingStreet}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.billingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingCountry}
                    touched={formik.touched.billingCountry}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.billingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingState}
                    touched={formik.touched.billingState}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.billingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingCity}
                    touched={formik.touched.billingCity}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="billingPostalCode"
                    type="number"
                    label={PostalCodeLabel}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.billingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingPostalCode}
                    touched={formik.touched.billingPostalCode}
                  />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5 d-flex justify-content-start align-items-center">
                  {ShippingAddressLabel}
                  <div className="d-flex flex-column align-items-center justify-content-start">
                    <div
                      className="form-check form-check-success form-switch form-switch-md ms-3"
                      dir="ltr"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input shadow-none"
                        id="customSwitchsizemd"
                        checked={sameAsBilling}
                        onChange={handleSameAsBilling}
                      />
                      <label
                        className="form-check-label custom-label fs-5"
                        htmlFor="customSwitchsizemd"
                      >
                        {SameAsBillingAddressLabel}
                      </label>
                      <i className="mdi mdi-help-circle-outline ms-1 mt-1 text-info fs-5 cursor-pointer align-middle tooltip-container-data">
                        <span className="tooltip-text font-weight-normal">
                          {SameAsBillingAddressTooltipLabel}
                        </span>
                      </i>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.shippingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingStreet}
                    touched={formik.touched.shippingStreet}
                    disabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.shippingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCountry}
                    touched={formik.touched.shippingCountry}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.shippingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingState}
                    touched={formik.touched.shippingState}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.shippingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCity}
                    touched={formik.touched.shippingCity}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingPostalCode"
                    type="number"
                    label={PostalCodeLabel}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.shippingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingPostalCode}
                    touched={formik.touched.shippingPostalCode}
                    disabled={sameAsBilling}
                  />
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/customer")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCustomer;
